import * as Ant from "antd";
import React from "react";
import styled from "styled-components";

import * as Color from "~/Color";
import * as Icon from "~/Icon";
import * as Layout from "~/Layout";
import * as Shadow from "~/Shadow";
import * as Typography from "~/Typography";

export type SizeConstrains = {
  readonly maxWidth?: string;
};

export type ImageCarouselProps = SizeConstrains & {
  readonly imageComponents: readonly JSX.Element[];
};

// TODO: Move this out to be a shared component and then modify the DVIR workflow to utilize this component.
export function ImageCarousel({
  imageComponents,
  maxWidth,
}: ImageCarouselProps) {
  const [imageIndex, setImageIndex] = React.useState(0);
  const carousel = React.useRef<{ readonly goTo: (index: number) => void }>(
    null
  );

  React.useEffect(() => {
    carousel.current?.goTo(imageIndex);
  }, [carousel, imageIndex]);

  const nextImage = () => {
    setImageIndex(imageIndex + 1);
  };

  const prevImage = () => {
    setImageIndex(imageIndex - 1);
  };

  return (
    <Layout.Block.Component data-testid="image-carousel">
      <Carousel.Title>
        <Typography.H4.Component bottom>Images</Typography.H4.Component>
        <Carousel.Count>
          {imageIndex + 1} of {imageComponents.length}
        </Carousel.Count>
      </Carousel.Title>
      <Carousel.Container maxWidth={maxWidth} data-testid="carousel-container">
        <Ant.Carousel
          dots={false}
          ref={carousel}
          className="image-carousel"
          style={{ overflow: "hidden" }}
        >
          {imageComponents}
        </Ant.Carousel>
        {
          <>
            {imageComponents.length > 1 && imageIndex > 0 && (
              <Carousel.Arrow
                data-testid="arrow-left"
                direction="previous"
                onClick={prevImage}
              >
                <Icon.ChevronLeft />
              </Carousel.Arrow>
            )}
            {imageComponents.length > 1 &&
              imageIndex < imageComponents.length - 1 && (
                <Carousel.Arrow
                  data-testid="arrow-right"
                  direction="next"
                  onClick={nextImage}
                >
                  <Icon.ChevronRight />
                </Carousel.Arrow>
              )}
            <Carousel.ImageOverlay className="overlay">
              <Typography.H3.Component className="preview-text">
                Click to preview
              </Typography.H3.Component>
            </Carousel.ImageOverlay>
          </>
        }
      </Carousel.Container>
    </Layout.Block.Component>
  );
}

export namespace Carousel {
  export const Container = styled.div<SizeConstrains>`
    position: relative;

    &:hover .overlay {
      opacity: 1;
    }

    .slick-slider * {
      width: ${({ maxWidth }) => maxWidth ?? "33vw"};
    }

    .preview-text {
      color: white;
      font-size: 20px;
      position: absolute;
      top: 50%;
      left: 50%;
      -webkit-transform: translate(-50%, -50%);
      -ms-transform: translate(-50%, -50%);
      transform: translate(-50%, -50%);
      text-align: center;
    }
  `;

  export const Count = styled.div`
    align-self: flex-end;
    font-weight: 600;
  `;

  export const Title = styled.div`
    display: flex;
    justify-content: space-between;
    padding-bottom: ${Layout.Block.padding()};
  `;

  export const Arrow = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;

    transform: translateY(-50%);
    position: absolute;
    top: 50%;
    ${({ direction }: { readonly direction: "previous" | "next" }) =>
      `${
        direction === "previous" ? "left" : "right"
      }: ${Layout.Inline.padding()};`}

    height: 50px;
    width: 50px;

    background: white;
    border: solid 1px ${() => Color.veryWhiteGrey()};
    border-radius: 100%;
    opacity: 0.65;

    ${Shadow.Box.styles(Shadow.medium())}

    font-size: 1.5em;
  `;

  export const ImageOverlay = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 90%;
    bottom: 0;
    left: 0;
    right: 0;
    height: 10%;
    width: 100%;
    opacity: 0;
    transition: 0.5s ease;
    background-color: rgba(0, 0, 0, 0.45);
    z-index: 100;
  `;
}
